import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const benefits = [
  { icon: "fas fa-bed", title: "Spacious layouts with multiple bedrooms" },
  { icon: "fas fa-square-parking", title: "Private outdoor areas and parking" },
  { icon: "fas fa-people-roof", title: "Family-friendly gated communities" },
  { icon: "fas fa-chart-line", title: "Strong rental demand and ROI potential" },
  { icon: "fas fa-dumbbell", title: "Access to shared amenities like parks, pools, and gyms" },
  { icon: "fas fa-house-circle-check", title: "Lifestyle benefits and long-term financial value" },
];

const townhouseTypes = [
  { icon: "fas fa-door-open", title: "Ready-to-Move Townhouses", text: "Perfect for buyers who want immediate occupancy or rental income." },
  { icon: "fas fa-file-signature", title: "Off-Plan Townhouses", text: "Ideal for investors looking for flexible payment plans and future appreciation." },
  { icon: "fas fa-gem", title: "Luxury Townhouses", text: "Premium designs with high-end finishes, smart features, and prime locations." },
  { icon: "fas fa-coins", title: "Affordable Townhouses", text: "Great entry-level options for first-time buyers and growing families." },
];

const faqs = [
  {
    icon: "fas fa-chart-line",
    question: "Are townhouses a good investment in Abu Dhabi?",
    answer: "Yes. They are. You can unhesitatingly buy a townhouse for you to live with your family or earn from it.",
  },
  {
    icon: "fas fa-globe",
    question: "Can foreigners buy townhouses in Abu Dhabi?",
    answer: "Yes. Foreigners can buy townhouses in Abu Dhabi, but only in designated areas. When they buy townhouses in those areas, they get full ownership rights.",
  },
  {
    icon: "fas fa-house",
    question: "What is the difference between a townhouse and a villa?",
    answer: "Townhouses share walls within a community, while villas are standalone properties with more private land.",
  },
  {
    icon: "fas fa-handshake",
    question: "Why should I choose NV Real Estate?",
    answer: "Nova Vision Real Estate is among the most trusted names for buying townhouses in Abu Dhabi.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Townhouse For Sale in Abu Dhabi", item: "https://nvrealestate.ae/townhouse-for-sale-abu-dhabi" },
  ],
};

function TownhouseForSaleAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Townhouse For Sale | Townhouses in Abu Dhabi For Sale</title>
        <meta
          name="description"
          content="Looking for the best townhouses in Abu Dhabi for sale? NV Real Estate is here to help you buy the best townhouse for sale in Abu Dhabi. Call now to discover why and buy."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/townhouse-for-sale-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi townhouse specialists</span>
              <h1 id="villa-page-title">Buy the Best Townhouse for Sale in Abu Dhabi</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Sale&city=&developer=&developer_name=&project=&project_name=&property_type=Townhouse&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore townhouses
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/townhouse-for-sale-abu-dhabi/02.png"
                alt="Townhouses for sale in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Exact match solutions</strong>
                <span>Helping you buy the right townhouse</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Buy the Best Townhouse for Sale in Abu Dhabi</h2>
              <p>
                Nova Vision Real Estate is named among the most prestigious real estate companies in
                Abu Dhabi. We have earned this great reputation by delivering outstanding real
                estate services to our clients. People count on us for our exact match solutions.
                When someone contacts us to buy a townhouse in Abu Dhabi, we listen to them
                carefully to understand their requirements and then help them buy the right
                townhouse for their needs.
              </p>
              <p>Connect with us now to buy the best townhouse for you in Abu Dhabi.</p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="why-townhouse-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why a townhouse</span>
              <h2 id="why-townhouse-heading">Why Choose a Townhouse in Abu Dhabi?</h2>
              <p>
                Abu Dhabi has many well-planned, modern, and future-ready communities that offer
                comfortable and luxurious living experiences. These residential properties are
                popular among property seekers who want more space than apartments but at more
                reasonable prices than villas.
              </p>
              <p>Here are some great benefits of buying a townhouse in Abu Dhabi-</p>
            </div>
            <div className="villa-benefits__grid">
              {benefits.map((benefit, index) => (
                <article className="villa-benefit-card" key={benefit.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={benefit.icon} /></span>
                  <h3>{benefit.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="types-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Townhouse types</span>
              <h2 id="types-heading">Townhouse Types for You</h2>
              <p>We bring all different types of townhouses for you. They are:</p>
            </div>
            <div className="villa-benefits__grid">
              {townhouseTypes.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              Our portfolio ensures you can find the right Townhouses in Abu Dhabi for sale without
              unnecessary complexity.
            </p>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Trust NV Real Estate?</h2>
                <p>
                  We have become a dependable real estate solutions provider in Abu Dhabi by
                  delivering outstanding real estate services to our clients. Here are some
                  important reasons that make us a trustworthy option in the city.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  Call us now to discuss your requirements to buy the right townhouse for sale in
                  Abu Dhabi.
                </p>
              </div>
              <ul>
                <li><strong>Market Expertise</strong> &mdash; We provide in-depth knowledge of Abu Dhabi&rsquo;s real estate trends and opportunities.</li>
                <li><strong>Exclusive Access</strong> &mdash; We connect you with premium and off-market properties not easily available elsewhere.</li>
                <li><strong>Transparency</strong> &mdash; All listings are verified, ensuring accurate and honest information.</li>
                <li><strong>End-to-End Support</strong> &mdash; From property selection to final paperwork, we guide you at every step.</li>
                <li><strong>Client-Focused Approach</strong> &mdash; We tailor recommendations based on your lifestyle, budget, and long-term goals.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Buyer questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your townhouse search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`townhouse-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`townhouse-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Townhouses</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Purchase Support</strong><span>Offer to Transfer</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also send your
              requirements to <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
      `}</style>
    </>
  );
}

export default TownhouseForSaleAbuDhabi;